import {computed, reactive, ref} from 'vue';

import type {IRequestError} from '@/assets/core/assets/api/types';
import {EApiRequestMethod} from '@/assets/core/assets/api/enums';

import doRequest, {
  getDetailsFromRequestError,
} from '@/assets/core/assets/api/doRequest';
import type {
  IRequestHook,
  IRequestHookResponseDetail,
  TRequestHookReturn,
} from '@/assets/core/assets/hooks/types';

const useRequestHook = (): IRequestHook => {
  const requestsDetail = reactive<Record<string, IRequestHookResponseDetail>>(
    {},
  );
  const requestsLastKey = ref<string | undefined>(undefined);

  const isLoading = computed(() => {
    return Object.values(requestsDetail).some((item) => item.loading);
  });

  const getRequestLastDetail = computed(() => {
    return requestsLastKey.value
      ? requestsDetail[requestsLastKey.value]
      : undefined;
  });

  const getKey = (url: URL | string, method?: EApiRequestMethod) => {
    return `${method ?? EApiRequestMethod.GET} ${url.toString()}`;
  };

  const doRequestCurrent = async (
    url: URL | string,
    method?: EApiRequestMethod,
    body?: unknown,
  ): TRequestHookReturn => {
    const key = getKey(url, method);
    requestsLastKey.value = key;

    const prevRequestDetails = requestsDetail[key];
    if (
      prevRequestDetails &&
      prevRequestDetails.loading &&
      prevRequestDetails.method === EApiRequestMethod.GET
    ) {
      return prevRequestDetails.promise;
    }

    const promise = doRequest(url, method, body);

    requestsDetail[key] = {
      method: method ?? EApiRequestMethod.GET,
      url: url,
      promise: promise,
      loading: true,
    };

    return await promise
      .then((data) => {
        requestsDetail[key] = {
          ...requestsDetail[key],
          loading: false,
          status: 200,
        };
        return data;
      })
      .catch((error: IRequestError | unknown) => {
        if (!(error as IRequestError).isUserError) throw error;

        const userError = error as IRequestError;
        requestsDetail[key] = {
          ...requestsDetail[key],
          loading: false,
          status: userError.response.status,
          error_detail: getDetailsFromRequestError(userError),
        };

        throw error;
      });
  };

  const getRequestDetail = (url: URL | string, method?: EApiRequestMethod) => {
    return requestsDetail[getKey(url, method)];
  };

  const getRequest = async (url: URL | string) => {
    return await doRequestCurrent(url, EApiRequestMethod.GET);
  };

  const postRequest = async (url: URL | string, body?: unknown) => {
    return await doRequestCurrent(url, EApiRequestMethod.POST, body);
  };

  const patchRequest = async (url: URL | string, body?: unknown) => {
    return await doRequestCurrent(url, EApiRequestMethod.PATCH, body);
  };

  const putRequest = async (url: URL | string, body?: unknown) => {
    return await doRequestCurrent(url, EApiRequestMethod.PUT, body);
  };

  const deleteRequest = async (url: URL | string) => {
    return await doRequestCurrent(url, EApiRequestMethod.DELETE);
  };

  return {
    isLoading,
    getRequestLastDetail,

    getRequestDetail,

    get: getRequest,
    post: postRequest,
    patch: patchRequest,
    put: putRequest,
    delete: deleteRequest,
  };
};

export default useRequestHook;
